import {
  type AuthCredential,
  linkWithCredential,
  type MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { useState } from 'react';
import { logLogin } from '../services/api';
import { auth } from '../services/firebase';

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

interface UseMfaProps {
  setIsLoggingIn: (val: boolean) => void;
  setAuthError: (error: string | null) => void;
  isLinkingMode: boolean;
  pendingCred: AuthCredential | null;
}

export const useMfa = ({
  setIsLoggingIn,
  setAuthError,
  isLinkingMode,
  pendingCred,
}: UseMfaProps) => {
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [isMfaChallengeVisible, setIsMfaChallengeVisible] = useState<boolean>(false);

  const startMfaSignIn = async (resolver: MultiFactorResolver) => {
    setMfaResolver(resolver);
    if (resolver.hints[0] && resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (_e) {
          // ignore error if already cleared
        }
        delete window.recaptchaVerifier;
      }

      // Ensure the container is empty to prevent 'already rendered' errors
      const container = document.getElementById('recaptcha-container-login');
      if (container) {
        container.innerHTML = '';
      }

      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-login', {
        size: 'invisible',
        callback: () => {
          /* ignore */
        },
      });
      try {
        const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          window.recaptchaVerifier
        );
        setVerificationId(newVerificationId);
        setIsMfaChallengeVisible(true);
      } catch (verifyError: unknown) {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          delete window.recaptchaVerifier;
        }
        const errorMessage =
          verifyError instanceof Error ? verifyError.message : String(verifyError);
        setAuthError(`Failed to send MFA code: ${errorMessage}`);
        setIsLoggingIn(false);
      }
    } else {
      setAuthError('Unsupported second factor. Please contact support.');
      setIsLoggingIn(false);
    }
  };

  const completeMfaSignIn = async (verificationCode: string): Promise<void> => {
    if (!mfaResolver || !verificationId) {
      setIsLoggingIn(false);
      throw new Error('MFA session is not available.');
    }

    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);

      if (isLinkingMode && pendingCred) {
        await linkWithCredential(userCredential.user, pendingCred);
      }

      logLogin().catch(() => {
        /* ignore */
      });

      setIsMfaChallengeVisible(false);
      setMfaResolver(null);
      setVerificationId(null);
      setAuthError(null);
      setIsLoggingIn(false);
    } catch (error) {
      setIsLoggingIn(false);
      throw error;
    }
  };

  const cancelMfaSignIn = (): void => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      delete window.recaptchaVerifier;
    }
    setIsMfaChallengeVisible(false);
    setMfaResolver(null);
    setVerificationId(null);
    setAuthError('Multi-factor authentication was cancelled.');
    setIsLoggingIn(false);
  };

  return {
    mfaResolver,
    isMfaChallengeVisible,
    startMfaSignIn,
    completeMfaSignIn,
    cancelMfaSignIn,
  };
};
